<div class="owner-shell">
  <aside class="owner-sidebar" [class.owner-sidebar--open]="menuOpen()">
    <div class="owner-workspace-menu owner-workspace-menu--sidebar" data-owner-workspace-menu>
      <button
        type="button"
        class="owner-workspace-trigger"
        [attr.aria-expanded]="workspaceMenuOpen()"
        aria-label="Switch workspace"
        title="Switch workspace"
        (click)="toggleWorkspaceMenu()"
      >
        <span class="owner-workspace-trigger__avatar" aria-hidden="true">{{ initials() }}</span>
        <span class="owner-workspace-trigger__text">
          <small>Organization</small>
          <strong>{{ orgName() }}</strong>
        </span>
        @if (canSwitchWorkspaces()) {
          <span class="material-symbols-outlined" aria-hidden="true">
            {{ workspaceMenuOpen() ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
          </span>
        }
      </button>

      @if (workspaceMenuOpen()) {
        <section class="owner-workspace-popover" role="menu">
          <header>
            <strong>Switch workspace</strong>
            <button type="button" aria-label="Close workspace menu" (click)="closeWorkspaceMenu()">
              <span class="material-symbols-outlined" aria-hidden="true">close</span>
            </button>
          </header>
          <div class="owner-workspace-list">
            @for (organization of workspaceOptions(); track organization.orgId) {
              <button
                type="button"
                class="owner-workspace-item"
                [class.owner-workspace-item--active]="organization.orgId === activeWorkspaceId()"
                role="menuitem"
                (click)="switchToWorkspace(organization)"
              >
                <span class="owner-workspace-item__avatar" aria-hidden="true">{{ workspaceInitials(organization.orgName) }}</span>
                <span>
                  <strong>{{ organization.orgName }}</strong>
                  <small>{{ workspaceRoleLabel(organization) }}</small>
                </span>
                @if (organization.orgId === activeWorkspaceId()) {
                  <span class="material-symbols-outlined owner-workspace-item__check" aria-hidden="true">check_circle</span>
                } @else {
                  <span class="material-symbols-outlined owner-workspace-item__arrow" aria-hidden="true">arrow_forward</span>
                }
              </button>
            }
          </div>
        </section>
      }
    </div>

    <nav class="owner-nav" aria-label="Owner navigation">
      @for (item of navItems; track item.route) {
        <a
          [routerLink]="item.route"
          routerLinkActive="owner-nav__item--active"
          [routerLinkActiveOptions]="{ exact: item.route === '/dashboard' }"
          class="owner-nav__item"
          (click)="closeMenu()"
        >
          <span class="material-symbols-outlined" aria-hidden="true">{{ item.icon }}</span>
          {{ item.label }}
        </a>
      }
    </nav>

    <div class="owner-sidebar__footer">
      <a routerLink="/support" routerLinkActive="owner-nav__item--active" class="owner-nav__item" (click)="closeMenu()">
        <span class="material-symbols-outlined" aria-hidden="true">support_agent</span>
        Support
      </a>
      <a routerLink="/settings" routerLinkActive="owner-nav__item--active" class="owner-nav__item" (click)="closeMenu()">
        <span class="material-symbols-outlined" aria-hidden="true">settings</span>
        Settings
      </a>
      <button type="button" class="owner-nav__item owner-nav__item--danger" (click)="signOut()">
        <span class="material-symbols-outlined" aria-hidden="true">logout</span>
        Sign out
      </button>
    </div>
  </aside>

  <main class="owner-main">
    <header class="owner-topbar">
      <button type="button" class="owner-icon-button owner-topbar__menu" (click)="menuOpen.set(!menuOpen())" aria-label="Toggle navigation">
        <span class="material-symbols-outlined" aria-hidden="true">menu</span>
      </button>
      <div>
        <strong>{{ orgName() }}</strong>
        <small>Portfolio owner access</small>
      </div>
      <div class="owner-topbar__actions">
        <div class="owner-notification-menu" data-owner-notifications-menu>
          <button
            type="button"
            class="owner-icon-button owner-notification-button"
            aria-label="Notifications"
            [attr.aria-expanded]="notificationsOpen()"
            title="Notifications"
            (click)="toggleNotifications()"
          >
            <span class="material-symbols-outlined" aria-hidden="true">notifications</span>
            @if (unreadNotificationCount() > 0) {
              <strong>{{ notificationCountLabel() }}</strong>
            }
          </button>

          @if (notificationsOpen()) {
            <section class="owner-notifications-popover" role="menu">
              <header>
                <span>
                  <strong>Notifications</strong>
                  <em>{{ unreadNotificationCount() }} unread</em>
                </span>
                <div>
                  <button type="button" (click)="markAllNotificationsRead()">Mark all read</button>
                  <button type="button" aria-label="Close notifications" (click)="closeNotifications()">
                    <span class="material-symbols-outlined" aria-hidden="true">close</span>
                  </button>
                </div>
              </header>

              @if (notificationsLoading()) {
                <div class="owner-notifications-state">
                  <span class="owner-notifications-spinner" aria-hidden="true"></span>
                  <p>Loading notifications...</p>
                </div>
              } @else if (notifications().length) {
                <div class="owner-notifications-list">
                  @for (notification of notifications(); track notification.id) {
                    <button
                      type="button"
                      class="owner-notification-item"
                      [class.owner-notification-item--unread]="!notification.read"
                      (click)="openNotification(notification)"
                    >
                      <span class="owner-notification-item__icon material-symbols-outlined" aria-hidden="true">{{ notificationIcon(notification) }}</span>
                      <span>
                        <strong>{{ notification.title }}</strong>
                        <em>{{ notificationMessage(notification) }}</em>
                        <small>
                          {{ notificationTimeLabel(notification) }}
                          <b>{{ notificationActionLabel(notification) }}</b>
                        </small>
                      </span>
                    </button>
                  }
                </div>
                <footer>
                  <button type="button" (click)="openNotificationsPage()">View all notifications</button>
                </footer>
              } @else {
                <div class="owner-notifications-state">
                  <span class="material-symbols-outlined" aria-hidden="true">notifications</span>
                  <h3>No notifications</h3>
                  <p>You are all caught up for now.</p>
                </div>
              }
            </section>
          }
        </div>
        <button type="button" class="owner-icon-button" (click)="toggleTheme()" [attr.aria-label]="themeLabel()" [attr.title]="themeLabel()">
          <span class="material-symbols-outlined" aria-hidden="true">{{ themeIcon() }}</span>
        </button>
      </div>
    </header>

    @if (ownerEntityRequired() && ownerEntities().length > 1 && ownerEntityId()) {
      <div class="owner-entity-context">
        <label for="owner-entity">Ownership entity</label>
        <p-select inputId="owner-entity" class="owner-entity-select" [options]="ownerEntities()"
                  optionLabel="displayName" optionValue="id" [ngModel]="ownerEntityId()"
                  (ngModelChange)="chooseOwnerEntity($event)" [disabled]="entitySwitching()"
                  appendTo="body" panelStyleClass="owner-entity-overlay">
          <ng-template #item let-entity>
            <span class="owner-entity-option">
              <strong>{{ entity.displayName }}</strong>
              <small>{{ entityDetail(entity) }}</small>
            </span>
          </ng-template>
        </p-select>
      </div>
    }
    @if (entityError()) { <p class="owner-entity-error" role="alert">{{ entityError() }}</p> }
    @if (invitationDashboard()) {
      <apex-workspace-invitations kind="owner" [gatewayUrl]="invitationEnvironment.apiGatewayUrl" [portalUrls]="invitationEnvironment" [timeZone]="dates.timeZone()" (pendingCount)="pendingInvitations.set($event)" />
    }
    @switch (mainState()) {
      @case ('ready') {
        <router-outlet />
      }
      @case ('loading') {
        <section class="owner-state" aria-live="polite">
          <span class="owner-state__spinner" aria-hidden="true"></span>
          <p>Loading your owner portal…</p>
        </section>
      }
      @case ('switching') {
        <section class="owner-state" aria-live="polite">
          <span class="owner-state__spinner" aria-hidden="true"></span>
          <p>Opening this ownership entity…</p>
        </section>
      }
      @case ('choose-entity') {
        <section class="owner-state" aria-labelledby="choose-entity-title">
          <h2 id="choose-entity-title">Choose which ownership to view</h2>
          <p>Your account owns property through more than one entity. Pick one to see its properties, statements and payouts. You can switch at any time.</p>
          <div class="owner-state__choices">
            @for (entity of ownerEntities(); track entity.id) {
              <button type="button" class="owner-state__choice" (click)="chooseOwnerEntity(entity.id)">
                <span class="material-symbols-outlined" aria-hidden="true">apartment</span>
                <span>
                  <strong>{{ entity.displayName }}</strong>
                  <small>{{ entity.propertyIds.length }} {{ entity.propertyIds.length === 1 ? 'property' : 'properties' }}</small>
                </span>
              </button>
            }
          </div>
        </section>
      }
      @case ('no-entities') {
        <section class="owner-state" role="status">
          <h2>No ownership is set up for you yet</h2>
          <p>Your property team hasn't finished linking your account to an ownership entity in this workspace. Ask them to check your owner invitation, then sign in again.</p>
          @if (canSwitchWorkspaces()) {
            <button type="button" class="owner-state__action" (click)="switchWorkspace()">Choose another workspace</button>
          }
        </section>
      }
      @case ('no-workspace') {
        <section class="owner-state" role="status">
          <h2>No owner workspace found</h2>
          <p>Your account isn't an owner in any workspace yet. Pending invitations appear on your dashboard. If none appears, ask your property team to check the email address they invited.</p>
          <button type="button" class="owner-state__action" (click)="switchWorkspace()">Choose a workspace</button>
        </section>
      }
      @case ('error') {
        <section class="owner-state" role="alert">
          <h2>We couldn't load your owner portal</h2>
          <p>Your access couldn't be checked just now. Check your connection and try again.</p>
          <button type="button" class="owner-state__action" (click)="reloadPortal()">Try again</button>
        </section>
      }
    }
  </main>
</div>
